import { Head, Link, router } from '@inertiajs/react'
import { UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { NotificationItem } from '@/types'

type Props = {
  notifications: NotificationItem[]
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = Math.max(0, now - then)
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function actorInitials(actor: NotificationItem['actor']): string {
  if (!actor) return '?'
  const first = actor.first_name?.[0] ?? ''
  const last = actor.last_name?.[0] ?? ''
  return (first + last) || actor.username[0].toUpperCase()
}

function actorName(actor: NotificationItem['actor']): string {
  if (!actor) return 'Someone'
  if (actor.first_name && actor.last_name) return `${actor.first_name} ${actor.last_name}`
  return `@${actor.username}`
}

function actorShowsFullName(actor: NotificationItem['actor']): boolean {
  return !!actor?.first_name && !!actor?.last_name
}

function NotificationRow({ notification }: { notification: NotificationItem }) {
  const { actor, notification_type, read_at, created_at } = notification
  const unread = !read_at
  const profileHref = actor?.profile_visible ? `/u/${actor.username}` : null

  let body: React.ReactNode = null
  if (notification_type === 'user_followed') {
    body = (
      <>
        <span className="font-semibold">{actorName(actor)}</span>
        {actorShowsFullName(actor) && actor && <span className="text-muted-foreground"> @{actor.username}</span>}
        <span className="text-foreground"> started following you.</span>
      </>
    )
  } else {
    body = <span className="text-muted-foreground">New activity</span>
  }

  const rowClassName = `flex items-center gap-4 rounded-2xl border border-black/5 bg-card p-4 shadow-xs ${profileHref ? 'transition-colors hover:bg-muted/40' : ''} ${unread ? 'ring-1 ring-primary/30' : ''}`
  const content = (
    <>
      <div className="relative shrink-0">
        <Avatar className="h-12 w-12">
          {actor?.avatar_url && <AvatarImage src={actor.avatar_url} alt={actor.username} />}
          <AvatarFallback>{actorInitials(actor)}</AvatarFallback>
        </Avatar>
        <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-card">
          <UserPlus className="size-3.5" />
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-base">{body}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{relativeTime(created_at)}</p>
        {!profileHref && actor && (
          <p className="mt-0.5 text-sm text-muted-foreground">This user keeps their profile private.</p>
        )}
      </div>
      {unread && <span className="ml-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
    </>
  )

  if (profileHref) {
    return (
      <Link href={profileHref} className={rowClassName}>
        {content}
      </Link>
    )
  }

  return <div className={rowClassName}>{content}</div>
}

export default function NotificationsIndex({ notifications }: Props) {
  const hasUnread = notifications.some(n => !n.read_at)

  return (
    <>
      <Head title="Notifications" />
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight">Notifications</h1>
          {hasUnread && (
            <Button
              variant="outline"
              onClick={() => router.post('/notifications/mark_all_read', {}, { preserveScroll: true })}
            >
              Mark all as read
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="rounded-2xl border border-black/5 bg-card p-10 text-center shadow-xs">
            <p className="text-base text-muted-foreground">You don&apos;t have any notifications yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(n => (
              <NotificationRow key={n.id} notification={n} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}
